import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { APP_TITLE, APP_LOGO } from "@/const";
import { Menu, X } from "lucide-react";
import { useState, useEffect } from "react";

export default function Header() {
  const [location] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location]);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About Us" },
    { href: "/blog", label: "Insights" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-md"
          : "bg-background/80 backdrop-blur-sm"
      }`}
    >
      <div className="container">
        <div className="flex h-16 items-center justify-between lg:h-20">
          {/* Logo */}
          <Link href="/">
            <a 
              className="flex items-center space-x-2 text-xl font-bold text-primary hover:text-primary/80 transition-colors lg:text-2xl"
              aria-label="Stanford Logistics Management Home"
            >
              <img src={APP_LOGO} alt={APP_TITLE} className="h-36" />
            </a>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <a
                  className={`px-4 py-2 text-sm font-medium transition-colors rounded-md lg:text-base min-h-[48px] flex items-center ${
                    location === link.href
                      ? "text-primary bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                  }`}
                  aria-label={`Navigate to ${link.label} page`}
                  aria-current={location === link.href ? "page" : undefined}
                >
                  {link.label}
                </a>
              </Link>
            ))}
            <Link href="/contact">
              <a>
                <Button 
                  className="ml-4 min-h-[48px] min-w-[48px] px-6 py-3" 
                  size="default"
                  aria-label="Contact Stanford Logistics Management"
                >
                  Get in Touch
                </Button>
              </a>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-3 min-w-[48px] min-h-[48px] flex items-center justify-center text-foreground hover:text-primary transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  <a
                    className={`block px-4 py-3 text-base font-medium transition-colors rounded-md min-h-[48px] flex items-center ${
                      location === link.href
                        ? "text-primary bg-primary/5"
                        : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                    }`}
                    aria-label={`Navigate to ${link.label} page`}
                    aria-current={location === link.href ? "page" : undefined}
                  >
                    {link.label}
                  </a>
                </Link>
              ))}
              <Link href="/contact">
                <a>
                  <Button 
                    className="w-full mt-2 min-h-[48px] min-w-[48px] py-3" 
                    size="lg"
                    aria-label="Contact Stanford Logistics Management"
                  >
                    Get in Touch
                  </Button>
                </a>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
